"use client";

/**
 * MethodArgsDisplay — read-only display of per-method args (mirrors FunctionInputs in display mode)
 *
 * Per §5.3 "Per-method args display via MethodArgsDisplay".
 * 9-way switch matching the 9 FunctionInputs components.
 * Displays as labeled value rows (not editable inputs).
 *
 * Amount formatting (G1-02):
 *   uint256 amount fields are stored on chain as wei and serialized as decimal
 *   strings by the backend. `formatEther` / `formatUnits` convert back to the
 *   display unit. `setFeePercent` stores `parseEther(fraction)` so the display
 *   inverse is `formatEther(value)` (e.g. 5% → "0.05").
 *
 * Labels (PM-1 2026-05-18): all field labels are Persian. PM ratified the
 * translation of the literal English labels from L14/L15.
 */

import { useTranslations } from "next-intl";
import { formatEther, formatUnits } from "viem";
import { WalletAddress } from "@/components/core/WalletAddress";

function safeBigInt(v: unknown): bigint | null {
  if (typeof v === "bigint") return v;
  if (typeof v === "number" && Number.isFinite(v)) return BigInt(Math.trunc(v));
  if (typeof v === "string" && v.trim().length > 0) {
    try {
      return BigInt(v.trim());
    } catch {
      return null;
    }
  }
  return null;
}

function formatEtherOrRaw(v: unknown): string {
  const big = safeBigInt(v);
  if (big === null) return "—";
  return formatEther(big);
}

function formatUnitsOrRaw(v: unknown, decimals: unknown): string {
  const big = safeBigInt(v);
  const dec =
    typeof decimals === "string" ? Number.parseInt(decimals, 10) : decimals;
  if (big === null || typeof dec !== "number" || !Number.isFinite(dec))
    return "—";
  return formatUnits(big, dec);
}

function formatIntegerOrRaw(v: unknown): string {
  const big = safeBigInt(v);
  if (big === null) return "—";
  return big.toString();
}

function stringify(
  v: unknown,
  boolLabels: { trueLabel: string; falseLabel: string },
): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean")
    return v ? boolLabels.trueLabel : boolLabels.falseLabel;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "bigint") return v.toString();
  return JSON.stringify(v);
}

interface ArgsDisplayRowProps {
  label: string;
  value: string;
  isAddress?: boolean;
}

function ArgsDisplayRow({
  label,
  value,
  isAddress = false,
}: ArgsDisplayRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-text-secondary">{label}</span>
      {isAddress ? (
        <WalletAddress address={value} size={16} canCopy />
      ) : (
        <span className="text-sm text-text-primary break-all" dir="ltr">
          {value}
        </span>
      )}
    </div>
  );
}

interface MethodArgsDisplayProps {
  functionSignature: string;
  inputs: unknown[];
}

export function MethodArgsDisplay({
  functionSignature,
  inputs,
}: MethodArgsDisplayProps) {
  const t = useTranslations("dao");
  const boolLabels = {
    trueLabel: t("proposals.detail.args.boolTrue"),
    falseLabel: t("proposals.detail.args.boolFalse"),
  };

  if (functionSignature === "setCalculator(address)") {
    return (
      <ArgsDisplayRow
        label={t("proposals.detail.args.calculatorAddress")}
        value={stringify(inputs[0], boolLabels)}
        isAddress
      />
    );
  }

  if (functionSignature === "setFeePercent(uint256)") {
    return (
      <ArgsDisplayRow
        label={t("proposals.detail.args.feePercent")}
        value={formatEtherOrRaw(inputs[0])}
      />
    );
  }

  if (functionSignature === "withdrawWithDao(uint256,address)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.amount")}
          value={formatEtherOrRaw(inputs[0])}
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[1], boolLabels)}
          isAddress
        />
      </div>
    );
  }

  if (functionSignature === "withdrawFeeByDao(uint256,address)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.amount")}
          value={formatEtherOrRaw(inputs[0])}
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[1], boolLabels)}
          isAddress
        />
      </div>
    );
  }

  if (functionSignature === "withdrawERC20(address,address,uint256)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.tokenAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[1], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.amount")}
          value={formatUnitsOrRaw(inputs[2], inputs[3])}
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.decimals")}
          value={stringify(inputs[3], boolLabels)}
        />
      </div>
    );
  }

  if (functionSignature === "withdrawETH(address,uint256)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.amount")}
          value={formatEtherOrRaw(inputs[1])}
        />
      </div>
    );
  }

  if (functionSignature === "withdrawERC721(address,address,uint256)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.tokenAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[1], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.tokenId")}
          value={formatIntegerOrRaw(inputs[2])}
        />
      </div>
    );
  }

  if (functionSignature === "changeCollectionStatus(address,bool,bool)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.collectionAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.mintStatus")}
          value={stringify(inputs[1], boolLabels)}
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.withdrawStatus")}
          value={stringify(inputs[2], boolLabels)}
        />
      </div>
    );
  }

  if (functionSignature === "addOrUpdateCollection(address,tuple,tuple[])") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.collectionAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.collectionInfo")}
          value={stringify(inputs[1], boolLabels)}
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.planTypes")}
          value={stringify(inputs[2], boolLabels)}
        />
      </div>
    );
  }

  // Takes no arguments — there is nothing to display, and falling through to
  // the unknown-method placeholder would wrongly imply the proposal is
  // unreadable.
  if (functionSignature === "calculateWeek()") {
    return null;
  }

  if (functionSignature === "userWithdrawWeekUVM(uint256)") {
    return (
      <ArgsDisplayRow
        label={t("proposals.detail.args.amount")}
        value={formatEtherOrRaw(inputs[0])}
      />
    );
  }

  if (functionSignature === "transfer(address,uint256)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.amount")}
          value={formatEtherOrRaw(inputs[1])}
        />
      </div>
    );
  }

  // Rendered as raw hex rather than through <WalletAddress>: that component is
  // built for 20-byte addresses and truncates, and a signer confirming proxy
  // governance must be able to read all 66 characters.
  if (
    functionSignature === "confirmBinding(bytes32)" ||
    functionSignature === "confirmUpgrade(bytes32)"
  ) {
    return (
      <ArgsDisplayRow
        label={t("proposals.detail.args.proposalId")}
        value={stringify(inputs[0], boolLabels)}
      />
    );
  }

  if (functionSignature === "setReferralPercent(uint256)") {
    return (
      <ArgsDisplayRow
        label={t("proposals.detail.args.referralPercent")}
        value={formatEtherOrRaw(inputs[0])}
      />
    );
  }

  if (functionSignature === "setMinimumPurchaseBv(uint256)") {
    return (
      <ArgsDisplayRow
        label={t("proposals.detail.args.minimumPurchaseBv")}
        value={formatEtherOrRaw(inputs[0])}
      />
    );
  }

  // Raw seconds, not ether-scaled — see MethodArgsDisplay's amount-formatting
  // note; this field never went through parseEther.
  if (functionSignature === "setDaoUvmWithdrawalCooldown(uint256)") {
    return (
      <ArgsDisplayRow
        label={t("proposals.detail.args.daoUvmWithdrawalCooldown")}
        value={formatIntegerOrRaw(inputs[0])}
      />
    );
  }

  if (functionSignature === "rescueERC20(address,address,uint256)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.tokenAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[1], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.amount")}
          value={formatUnitsOrRaw(inputs[2], inputs[3])}
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.decimals")}
          value={stringify(inputs[3], boolLabels)}
        />
      </div>
    );
  }

  if (functionSignature === "rescueERC721(address,address,uint256)") {
    return (
      <div className="flex flex-col gap-3">
        <ArgsDisplayRow
          label={t("proposals.detail.args.tokenAddress")}
          value={stringify(inputs[0], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.destinationAddress")}
          value={stringify(inputs[1], boolLabels)}
          isAddress
        />
        <ArgsDisplayRow
          label={t("proposals.detail.args.tokenId")}
          value={formatIntegerOrRaw(inputs[2])}
        />
      </div>
    );
  }

  return (
    <div className="text-sm text-text-secondary">
      {t("proposals.detail.args.unknownMethod")}
    </div>
  );
}
